import React, { useState } from "react";
// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { keyframes } from "@emotion/react";
import { 
  MdOutlineVerifiedUser, 
  MdOutlineMarkEmailRead, 
  MdOutlineVerified, 
  MdOutlineSecurity,
  MdOutlineEmail,
  MdLockOpen
} from "react-icons/md";
import { FaShieldAlt, FaUserShield, FaFingerprint } from "react-icons/fa";
import useAxios from "axios-hooks";
import { useSetRecoilState } from "recoil";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";

import DefaultAuth from "layouts/auth/Default";
import illustration from "assets/img/auth/auth.png";
import Footer from "components/footer/FooterAuth";
import LOGO from "../../../assets/img/dark-mm-logo.svg";

// Define animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;
const OtpVerificationPage = () => {
  // Chakra color mode
  //const textColor = useColorModeValue("navy.700", "white");
  const textColor = "white";
  //const textColorSecondary = "gray.400";
  const textColorSecondary = "white";
  const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
  const textColorBrand = useColorModeValue("brand.500", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const googleBg = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
  const googleText = useColorModeValue("navy.700", "white");
  const [isLoading, setIsLoading] = useState(false);
  const googleHover = useColorModeValue(
    { bg: "gray.200" },
    { bg: "whiteAlpha.300" }
  );
  const googleActive = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.200" }
  );

  const setToast = useSetRecoilState(toastState);
  const navigate = useNavigate();
  const location = useLocation();

  const schema = yup.object().shape({
    Otp: yup.string().min(6).max(6).required("Otp is Required"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    register,
    setValue,
    getValues,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const [{ data, loading, error }, execute] = useAxios(
    { method: "POST", url: `/api/auth/verifyotp` },
    { manual: true }
  );

  const submitHandler = async () => {
    setIsLoading(true);
    const formValues = getValues();
    try {
      let reqData = {};
      reqData["otp"] = parseInt(formValues?.Otp);
      reqData["email"] = location?.state?.email;
      const response = await execute({ data: reqData });
      logger.log(response);
      navigate("/auth/resetPassword", {
        state: {
          email: location?.state?.email,
          encodedString: response?.data?.data,
        },
      });
      setToast({
        isOpen: true,
        title: `Otp Verification Success`,
        status: "success",
      });
    } catch (error) {
      if (error?.code !== "ERR_CANCELED") {
        setToast({
          isOpen: true,
          title: "Error in verfiying OTP",
          status: "error",
        });
      }
    }

    setIsLoading(false);
  };

  return (
    <DefaultAuth>
      {/* Split background - dark on left, white on right */}
      <Box
        position="absolute"
        top="0"
        left="0"
        width={{ base: "100%", md: "50%" }}
        height="100%"
        bg="#151c24"
        zIndex="-1"
      />
      <Box
        position="absolute"
        top="0"
        right="0"
        width={{ base: "0%", md: "50%" }}
        height="100%"
        bg="white"
        zIndex="-1"
        display={{ base: "none", md: "block" }}
      />

      {/* Centered glassmorphic card for OTP verification form */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        bg="rgba(21, 28, 36, 0.75)"
        borderRadius="2xl"
        boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.18)"
        backdropFilter="blur(18px)"
        border="1.5px solid rgba(35, 169, 121, 0.15)"
        w={["90%", "85%", "80%", "80%"]}
        maxW="450px"
        p={8}
        position="absolute"
        left={{ base: "50%", md: "25%" }}
        transform="translateX(-50%)"
        zIndex={1}
        _before={{
          content: '""',
          position: "absolute",
          top: "-2px",
          left: "-2px",
          right: "-2px",
          bottom: "-2px",
          borderRadius: "2xl",
          border: "2px solid #23A979",
          opacity: 0.1,
          zIndex: -1,
          pointerEvents: "none",
        }}
      >
        <Flex direction="column" align="center" justify="center" w="100%">
          <Image 
            src={LOGO} 
            alt="MyMotor Logo" 
            width="auto"
            height="80px"
            mb={6} 
            filter="drop-shadow(0 2px 8px rgba(0, 0, 0, 0.4))" 
            objectFit="contain"
            preserveAspectRatio="xMidYMid meet"
          />
          <Heading color={textColor} fontSize="2xl" mb={2} fontWeight="bold" letterSpacing={1.2}>
            Verify OTP
          </Heading>
          <Text color="brand.500" fontSize="lg" mb={1} fontWeight="semibold" letterSpacing={0.6}>
            Security Verification
          </Text>
          <Text color="gray.300" fontSize="md" mb={8} textAlign="center">
            Enter the 6-digit code sent to your email
          </Text>

          <form onSubmit={handleSubmit(submitHandler)} style={{ width: "100%" }}>
            <Controller
              name="Otp"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <FormControl isInvalid={errors.Otp} mb={6}>
                  <FormLabel color={textColor} fontWeight="semibold">
                    One-Time Password
                  </FormLabel>
                  <InputGroup>
                    <Input
                      {...field}
                      type="number"
                      placeholder="Enter 6-digit OTP"
                      bg="rgba(255,255,255,0.13)"
                      border="1.5px solid rgba(35, 169, 121, 0.22)"
                      color={textColor}
                      fontSize="lg"
                      py={6}
                      px={5}
                      borderRadius="xl"
                      maxLength={6}
                      _focus={{
                        borderColor: "brand.500",
                        boxShadow: `0 0 0 2px #23A979`,
                        bg: "rgba(255,255,255,0.22)",
                      }}
                      transition="all 0.18s cubic-bezier(.4,2,.6,1)"
                    />
                    <InputRightElement pointerEvents="none">
                      <Icon as={MdOutlineVerifiedUser} color="brand.500" boxSize={6} />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.Otp && errors.Otp.message}</FormErrorMessage>
                </FormControl>
              )}
            />

            <Flex justify="space-between" align="center" mt={6} mb={8} w="100%">
              <Button
                type="submit"
                w="60%"
                py={6}
                fontSize="lg"
                fontWeight="bold"
                color="white"
                bg="brand.500"
                boxShadow="0 2px 16px 0 rgba(35, 169, 121, 0.18)"
                borderRadius="xl"
                _hover={{
                  bg: "brand.500",
                  boxShadow: "0 4px 24px 0 rgba(35, 169, 121, 0.29)",
                  transform: "scale(1.03)",
                }}
                _active={{
                  bg: "brand.500",
                  boxShadow: "0 2px 12px 0 rgba(35, 169, 121, 0.16)",
                }}
                transition="all 0.18s cubic-bezier(.4,2,.6,1)"
                isLoading={isLoading}
                loadingText="Verifying..."
              >
                Verify OTP
              </Button>
              <Text
                as="button"
                color="brand.500"
                fontSize="sm"
                fontWeight="medium"
                bg="none"
                border="none"
                cursor="pointer"
                _hover={{ textDecoration: "underline", color: "brand.600" }}
                onClick={() => navigate("/auth/forgotpassword")}
                transition="color 0.15s"
                type="button"
              >
                Back to Forgot Password
              </Text>
            </Flex>
          </form>
        </Flex>
      </Flex>

      {/* Right side illustration with animated icons - hidden on mobile */}
      <Box
        position="absolute"
        top="0"
        right="0"
        width="50%"
        height="100%"
        zIndex={0}
        display={{ base: "none", md: "none", lg: "flex" }}
        alignItems="center"
        justifyContent="center"
        bg="white"
      >
        <Box
          width="80%"
          maxW="600px"
          height="auto"
          bg="white"
          borderRadius="2xl"
          boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.08)"
          border="1.5px solid rgba(35, 169, 121, 0.15)"
          p={8}
          textAlign="center"
        >
          <Box 
            position="relative"
            h="280px"
            mb={5}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {/* Main verification icon with pulse animation */}
            <Box
              position="relative"
              animation={`${pulse} 2s infinite ease-in-out`}
            >
              <Icon 
                as={MdOutlineVerified} 
                boxSize={36} 
                color="brand.500"
                filter="drop-shadow(0 8px 20px rgba(35, 169, 121, 0.3))"
              />
            </Box>
            
            {/* Circle of icons around the main icon */}
            <Box position="absolute" top="50%" left="50%" w="240px" h="240px" transform="translate(-50%, -50%)">
              {/* Email icon */}
              <Box position="absolute" top="0" left="50%" transform="translateX(-50%)" animation={`${float} 3s infinite ease-in-out`}>
                <Icon as={MdOutlineEmail} boxSize={10} color="#3182CE" />
              </Box>
              
              {/* Shield icon */}
              <Box position="absolute" top="25%" right="0" animation={`${float} 3s infinite ease-in-out 0.5s`}>
                <Icon as={FaUserShield} boxSize={10} color="#38A169" />
              </Box>
              
              {/* Email verified icon */}
              <Box position="absolute" bottom="0" left="50%" transform="translateX(-50%)" animation={`${float} 3s infinite ease-in-out 1s`}>
                <Icon as={MdOutlineMarkEmailRead} boxSize={10} color="#805AD5" />
              </Box>
              
              {/* Fingerprint icon */}
              <Box position="absolute" top="25%" left="0" animation={`${float} 3s infinite ease-in-out 1.5s`}>
                <Icon as={FaFingerprint} boxSize={10} color="#319795" />
              </Box>
              
              {/* Security icon */}
              <Box position="absolute" bottom="25%" right="0" animation={`${float} 3s infinite ease-in-out 2s`}>
                <Icon as={MdOutlineSecurity} boxSize={10} color="#E53E3E" />
              </Box>
              
              {/* Shield icon */}
              <Box position="absolute" bottom="25%" left="0" animation={`${float} 3s infinite ease-in-out 2.5s`}>
                <Icon as={FaShieldAlt} boxSize={10} color="#D53F8C" />
              </Box>
            </Box>
          </Box>
          
          <Heading color="#2D3748" fontSize="2xl" mb={3} fontWeight="600">
            Verification Required
          </Heading>
          <Text color="rgba(45, 55, 72, 0.8)" fontSize="lg">
            We've sent a security code to your email for verification
          </Text>
        </Box>
      </Box>
    </DefaultAuth>
  );
};

export default OtpVerificationPage;
